import React, { useEffect, useMemo, useState } from "react";
import LoadingOverlay from "../../../components/Ui/LoadingOverlay";
import SearchInput from "../../../components/Ui/Search";
import Pagination from "../../../components/Ui/Paging";
import { calculatePage } from "../../../utils/paging";
import { IconPlus, IconTrash, IconEdit } from "../../../components/Ui/Icon";
import Create from "./Create";
import { adminGetVideos, adminDeleteVideo } from "../../../services/video";

const List = () => {
  const [loading, setLoading] = useState(false);
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [refresh, setRefresh] = useState(new Date());

  const [paging, setPaging] = useState({
    page: 1,
    limit: 10, // Back to normal limit
    sortBy: "createdAt",
    sortType: "DESC",
    search: "",
    total: 0,
    totalPages: 0,
  });

  const [data, setData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await adminGetVideos({
          page: paging.page,
          limit: paging.limit,
          sortBy: paging.sortBy,
          sortType: paging.sortType,
          search: paging.search,
        });
        const rows = res?.data?.data || [];
        const total = res?.data?.total || 0;
        const totalPages = calculatePage(total, paging.limit);
        
        
        setData(rows);
        setPaging((prev) => ({
          ...prev,
          total,
          totalPages,
        }));
      } catch (e) {
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paging.page, paging.limit, paging.sortBy, paging.sortType, paging.search, refresh]);

  const handlePageChange = (page) => {
    setPaging((prev) => ({ ...prev, page }));
  };

  const onSearch = (search) => {
    setPaging((prev) => ({ ...prev, search }));
  };

  const handleDelete = async (id) => {
    // eslint-disable-next-line no-alert
    const yes = window.confirm("Delete this video?");
    if (!yes) return;
    setLoading(true);
    try {
      await adminDeleteVideo(id);
      setRefresh(new Date());
    } catch (e) {}
    setLoading(false);
  };

  const filtered = useMemo(() => {
    const s = (paging.search || "").toLowerCase();
    const arr = !s
      ? data
      : data.filter((x) => x.title?.toLowerCase().includes(s));
    const total = arr.length;
    const totalPages = calculatePage(total, paging.limit);
    const start = (paging.page - 1) * paging.limit;
    const end = start + paging.limit;
    return {
      total,
      totalPages,
      list: arr.slice(start, end),
    };
  }, [data, paging.page, paging.limit, paging.search]);

  const dataSection = useMemo(() => {
    return (
      <table className="w-full text-sm text-left rtl:text-right text-gray-500">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3">STT</th>
            <th scope="col" className="px-6 py-3">Title</th>
            <th scope="col" className="px-6 py-3">URL</th>
            <th scope="col" className="px-6 py-3">Created At</th>
            <th scope="col" className="px-6 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={item._id || item.id || `${index}-${item.url}`} className="bg-white border-b ">
              <td className="px-6 py-4">{(paging.page - 1) * paging.limit + index + 1}</td>
              <td className="px-6 py-4">{item.title}</td>
              <td className="px-6 py-4">
                <a className="text-blue-600 hover:underline" href={item.url} target="_blank" rel="noreferrer">{item.url}</a>
              </td>
              <td className="px-6 py-4">{new Date(item.createdAt).toLocaleString()}</td>
              <td className="px-6 py-4">
                <button
                  onClick={() => { setSelectedItem(item); setIsOpenCreate(true); }}
                  title="Edit"
                  className="text-white mr-2 bg-slate-400 hover:bg-slate-500 rounded focus:ring-4 focus:ring-blue-300 font-medium text-sm px-2.5 py-1 text-center"
                >
                  <IconEdit className="size-4" />
                </button>
                <button
                  onClick={() => handleDelete(item._id)}
                  title="Delete"
                  className="text-white bg-slate-400 hover:bg-slate-500 rounded focus:ring-4 focus:ring-blue-300 font-medium text-sm px-2.5 py-1 text-center"
                >
                  <IconTrash className="size-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }, [data, paging.page, paging.limit]);

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <div className="flex w-full justify-between items-center p-3 flex-wrap md:flex-nowrap">
        <h2 className="font-semibold text-3xl text-left rtl:text-right text-gray-900">Videos</h2>
        <div className="inline-flex items-center gap-2">
          <a
            href="#create"
            onClick={(e) => { e.preventDefault(); setIsOpenCreate(true); }}
            type="button"
            className="flex items-center p-2 text-sm font-medium h-full text-white bg-blue-700 rounded-lg border border-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300"
          >
            <IconPlus />
            <span> Create</span>
          </a>
          <SearchInput className="md:mr-28" onSubmitSearch={onSearch} />
        </div>
      </div>
      {dataSection}
      {paging.totalPages > 1 && (
        <Pagination
          totalPages={paging.totalPages}
          currentPage={paging.page}
          onPageChange={handlePageChange}
        />
      )}
      {loading && <LoadingOverlay />}

      {isOpenCreate && (
        <Create
          data={selectedItem}
          onSubmited={(payload) => {
            // Trigger reload from server after create/update
            setSelectedItem(null);
            setRefresh(new Date());
          }}
          togglePopup={() => { setSelectedItem(null); setIsOpenCreate(false); }}
        />
      )}
    </div>
  );
};

export default List;


